import {
  Directive,
  ElementRef,
  inject,
  input,
  DOCUMENT,
  signal
} from '@angular/core';
import { Subject } from 'rxjs';

import { FlyoutMenuComponent } from './flyout-menu.component';
import { FLYOUT_MENU_HOST, type FlyoutMenuHost, type FlyoutMenuItem } from './flyout-menu.contract';

import type { OnDestroy, InputSignal, WritableSignal } from '@angular/core';
import type { FocusableOption } from '@angular/cdk/a11y';

@Directive({
  selector: '[tdWbFlyoutMenuItem]',
  host: {
    'class': 'td-wb-flyout-menu__item',
    '[class.td-wb-flyout-menu__item-highlighted]': 'highlighted()',
    '[class.td-wb-flyout-menu__item-trigger]': 'triggersSubmenu()',
    '[attr.tabindex]': 'isDisabled() ? \'-1\' : \'0\'',
    '[attr.role]': '"menuitem"',
    '[attr.disabled]': 'isDisabled() ? \'disabled\' : null',
    '[attr.aria-disabled]': 'isDisabled().toString()',
    '(click)': 'onClick($event)'
  }
})
export class FlyoutMenuItemDirective implements OnDestroy, FocusableOption, FlyoutMenuItem {
  /**
   * optional: in case you need to focus on a specific element,
   *   set this value and use selectedKey to match on td-wb-flyout-menu
   */
  readonly key: InputSignal<string | undefined> = input<string>(undefined, { alias: 'key' });

  readonly isDisabled: InputSignal<boolean> = input<boolean>(false, { alias: 'disabled' });

  get disabled(): boolean {
    return this.isDisabled();
  }

  readonly highlighted: WritableSignal<boolean> = signal<boolean>(false);

  readonly triggersSubmenu: WritableSignal<boolean> = signal<boolean>(false);

  readonly clickedSubject: Subject<FlyoutMenuItem> = new Subject<FlyoutMenuItem>();

  private readonly menu: FlyoutMenuHost | FlyoutMenuComponent | null = inject(FLYOUT_MENU_HOST, { optional: true })
    ?? inject(FlyoutMenuComponent, { optional: true });

  private readonly document = inject(DOCUMENT);
  private readonly elementRef = inject(ElementRef);

  constructor() {
    if (this.menu !== null) {
      this.menu.addItem(this);
    }
  }

  onClick(event: Event): void {
    if (this.isDisabled()) {
      event.preventDefault();
      event.stopPropagation();
    } else {
      this.clickedSubject.next(this);
    }
  }

  ngOnDestroy(): void {
    if (this.menu !== null) {
      this.menu.removeItem(this);
    }
    this.clickedSubject.complete();
  }

  /** Focuses the menu item. */
  focus(): void {
    this.elementRef.nativeElement.focus({ preventScroll: true });
  }

  /**
   * Gets the label to be used when determining
   * whether the option should be focused.
   */
  getLabel(): string {
    const element: HTMLElement = this.elementRef.nativeElement;
    const textNodeType = this.document !== null ? this.document.TEXT_NODE : 3;
    let output = '';

    if (element.childNodes !== null && element.childNodes !== undefined) {
      const length = element.childNodes.length;

      // Go through all the top-level text nodes and extract their text.
      // We skip anything that's not a text node to prevent the text from
      // being thrown off by something like an icon.
      for (let i = 0; i < length; i++) {
        if (element.childNodes[i].nodeType === textNodeType) {
          output += element.childNodes[i].textContent;
        }
      }
    }
    return output.trim();
  }
}
